import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate, useParams } from 'react-router-dom';
import {
  Search as SearchIcon,
  SlidersHorizontal,
  X,
  ChevronDown,
  ChevronUp,
  Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { ProductCard } from '@/components/product/card';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import SearchFilters from '@/components/ui/SearchFilters';
import { SearchSeoHead } from '@/components/common/SearchSeoHead';
import { extractSearchCategoryId } from '@/utils/slugUtils';

const ITEMS_PER_PAGE = 10;

// Define the listing type to solve the TypeScript issues
interface Listing {
  id: number;
  title: string;
  description: string;
  price: number;
  location: string;
  images?: string[];
  categories?: { name: string };
  is_premium: boolean;
  is_sold: boolean;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  make?: string;
  model?: string;
  year?: number;
  category_id?: number;
  delivery_option?: string;
  [key: string]: any; // For other properties that might exist
}

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categoryIdSlug } = useParams();
  const navigate = useNavigate();
  
  // Reference for infinite scrolling
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Get search parameters
  const query = searchParams.get('q') || '';
  let categoryParam = searchParams.get('category') || '';
  
  // Extract category ID from URL parameter if present
  if (categoryIdSlug && !categoryParam) {
    categoryParam = extractSearchCategoryId(categoryIdSlug);
  }
  
  const sortBy = searchParams.get('sort') || 'newest';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';
  const location = searchParams.get('location') || '';
  const isPremium = searchParams.get('premium') === 'true';
  const transportOption = searchParams.get('transport') || '';

  // Vehicle-specific parameters
  const make = searchParams.get('make') || '';
  const minYear = searchParams.get('minYear') || '';
  const maxYear = searchParams.get('maxYear') || '';

  // Local state for filters
  const [searchQuery, setSearchQuery] = useState(query);
  const [priceRange, setPriceRange] = useState<[number, number]>([
    minPrice ? parseInt(minPrice) : 0,
    maxPrice ? parseInt(maxPrice) : 1000000
  ]);
  const [selectedLocation, setSelectedLocation] = useState(location);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedMake, setSelectedMake] = useState(make);
  const [yearRange, setYearRange] = useState<[number, number]>([
    minYear ? parseInt(minYear) : 1970,
    maxYear ? parseInt(maxYear) : new Date().getFullYear()
  ]);
  const [selectedTransportOption, setSelectedTransportOption] = useState(transportOption);
  const [showPremiumOnly, setShowPremiumOnly] = useState(isPremium);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    const resolveCategory = async () => {
      if (categoryParam && categories) {
        // Check if it's already a number
        if (/^\d+$/.test(categoryParam)) {
          setSelectedCategoryId(categoryParam);
        } else {
          // Try to find the category by name or slug
          const foundCategory = categories.find(
            (cat) =>
              cat.name.toLowerCase() === categoryParam.toLowerCase() ||
              cat.slug.toLowerCase() === categoryParam.toLowerCase()
          );
          if (foundCategory) {
            setSelectedCategoryId(foundCategory.id.toString());
            // Update URL parameter to use ID instead of name for consistency
            if (!categoryIdSlug) {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('category', foundCategory.id.toString());
              setSearchParams(newParams);
            }
          }
        }
      }
    };
    resolveCategory();
  }, [categoryParam, categories, searchParams, setSearchParams, categoryIdSlug]);

  // Infinite query for listings with pagination
  const {
    data: listingsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery<Listing[]>({
    queryKey: [
      'searchListings',
      query,
      selectedCategoryId,
      sortBy,
      minPrice,
      maxPrice,
      location,
      isPremium,
      make,
      minYear,
      maxYear,
      transportOption,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      let queryBuilder = supabase
        .from('listings')
        .select('*, categories(name)')
        .eq('is_sold', false)
        .eq('is_active', true)
        .eq('is_deleted', false);

      // Apply filters
      if (query) {
        // Enhanced fuzzy search: search in both title and description
        queryBuilder = queryBuilder.or(`title.ilike.%${query}%,description.ilike.%${query}%`);
      }
      
      if (selectedCategoryId && !isNaN(parseInt(selectedCategoryId))) {
        queryBuilder = queryBuilder.eq('category_id', parseInt(selectedCategoryId));
      }
      
      if (minPrice) {
        queryBuilder = queryBuilder.gte('price', parseInt(minPrice));
      }
      
      if (maxPrice) {
        queryBuilder = queryBuilder.lte('price', parseInt(maxPrice));
      }
      
      if (location) {
        queryBuilder = queryBuilder.ilike('location', `%${location}%`);
      }
      
      if (isPremium) {
        queryBuilder = queryBuilder.eq('is_premium', true);
      }
      
      // Transport filter
      if (transportOption) {
        queryBuilder = queryBuilder.eq('delivery_option', transportOption);
      }
      
      // Vehicle-specific filters
      if (make) {
        queryBuilder = queryBuilder.ilike('make', `%${make}%`);
      }
      
      if (minYear) {
        queryBuilder = queryBuilder.gte('year', parseInt(minYear));
      }
      
      if (maxYear) {
        queryBuilder = queryBuilder.lte('year', parseInt(maxYear));
      }

      // Apply sorting
      switch (sortBy) {
        case 'price_asc':
          queryBuilder = queryBuilder.order('is_premium', { ascending: false }).order('price', { ascending: true });
          break;
        case 'price_desc':
          queryBuilder = queryBuilder.order('is_premium', { ascending: false }).order('price', { ascending: false });
          break;
        case 'oldest':
          queryBuilder = queryBuilder.order('is_premium', { ascending: false }).order('created_at', { ascending: true });
          break;
        case 'newest':
        default:
          queryBuilder = queryBuilder.order('is_premium', { ascending: false }).order('created_at', { ascending: false });
          break;
      }
      
      // Implement pagination: limit and range
      queryBuilder = queryBuilder.range(pageParam as number, (pageParam as number) + ITEMS_PER_PAGE - 1);
      
      const { data, error } = await queryBuilder;
      
      if (error) throw error;
      return data || [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < ITEMS_PER_PAGE) return undefined;
      return allPages.length * ITEMS_PER_PAGE;
    },
    enabled: true,
  });

  // Setup IntersectionObserver for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.5 } // Trigger when 50% of the element is visible
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Flatten pages for rendering
  const listings = listingsData?.pages.flat() || [];

  const applyFilters = () => {
    const newParams = new URLSearchParams();
    if (searchQuery) newParams.set('q', searchQuery);
    if (selectedCategoryId) newParams.set('category', selectedCategoryId);
    if (sortBy) newParams.set('sort', sortBy);
    if (priceRange[0] > 0) newParams.set('minPrice', priceRange[0].toString());
    if (priceRange[1] < 1000000) newParams.set('maxPrice', priceRange[1].toString());
    if (selectedLocation) newParams.set('location', selectedLocation);
    if (showPremiumOnly) newParams.set('premium', 'true');
    if (selectedTransportOption) newParams.set('transport', selectedTransportOption);
    // Vehicle-specific filters
    if (selectedMake) newParams.set('make', selectedMake);
    if (yearRange[0] > 1970) newParams.set('minYear', yearRange[0].toString());
    if (yearRange[1] < new Date().getFullYear()) newParams.set('maxYear', yearRange[1].toString());
    setSearchParams(newParams);
    setIsMobileFiltersOpen(false);
    refetch(); // Optionally refetch listings based on new filters
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setPriceRange([0, 1000000]);
    setSelectedLocation('');
    setSelectedCategoryId('');
    setSelectedMake('');
    setYearRange([1970, new Date().getFullYear()]);
    setShowPremiumOnly(false);
    setSelectedTransportOption('');
    const newParams = new URLSearchParams();
    if (query) newParams.set('q', query);
    setSearchParams(newParams);
    refetch();
  };

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set('q', searchQuery);
    } else {
      newParams.delete('q');
    }
    setSearchParams(newParams);
  };

  // Handle transport option change
  const handleTransportOptionChange = (value: string) => {
    if (value === selectedTransportOption) {
      setSelectedTransportOption('');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('transport');
      setSearchParams(newParams);
    } else {
      setSelectedTransportOption(value);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('transport', value);
      setSearchParams(newParams);
    }
  };

  // Count active filters function
  const getActiveFiltersCount = () => {
    let count = 0;
    if (minPrice) count++;
    if (maxPrice) count++;
    if (location) count++;
    if (isPremium) count++;
    if (selectedCategoryId) count++;
    if (make) count++;
    if (minYear) count++;
    if (maxYear) count++;
    if (transportOption) count++;
    return count;
  };

  // Handle category change
  const handleCategoryChange = (categoryId: string) => {
    if (categoryId === selectedCategoryId) {
      setSelectedCategoryId('');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('category');
      setSearchParams(newParams);
    } else {
      setSelectedCategoryId(categoryId);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('category', categoryId);
      setSearchParams(newParams);
    }
  };

  // Handle make change for vehicles
  const handleMakeChange = (makeValue: string) => {
    if (makeValue === selectedMake) {
      setSelectedMake('');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('make');
      setSearchParams(newParams);
    } else {
      setSelectedMake(makeValue);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('make', makeValue);
      setSearchParams(newParams);
    }
  };

  // Handle year range change for vehicles
  const handleYearChange = (minYearValue: number, maxYearValue: number) => {
    setYearRange([minYearValue, maxYearValue]);
    const newParams = new URLSearchParams(searchParams);
    if (minYearValue > 1970) {
      newParams.set('minYear', minYearValue.toString());
    } else {
      newParams.delete('minYear');
    }
    if (maxYearValue < new Date().getFullYear()) {
      newParams.set('maxYear', maxYearValue.toString());
    } else {
      newParams.delete('maxYear');
    }
    setSearchParams(newParams);
  };

  // Active category name
  const activeCategoryName =
    categories?.find((c) => c.id.toString() === selectedCategoryId)?.name || '';

  // Return component JSX
  return (
    <div className="flex flex-col min-h-screen">
      <SearchSeoHead
        query={query}
        categoryName={activeCategoryName}
        categoryId={selectedCategoryId}
        location={location}
        minPrice={minPrice ? parseInt(minPrice) : undefined}
        maxPrice={maxPrice ? parseInt(maxPrice) : undefined}
        resultsCount={listings.length}
        sortBy={sortBy}
        filters={{
          make: selectedMake,
          minYear: minYear ? parseInt(minYear) : undefined,
          maxYear: maxYear ? parseInt(maxYear) : undefined,
          condition: undefined, // We'll need to track this if it becomes a filter
          transportOption: selectedTransportOption
        }}
      />
      
      <Navbar />
      
      <main className="flex-grow pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search Header */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-grow">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Kërko produkte..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full"
                />
              </div>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600">
                Kërko
              </Button>
            </form>
          </div>

          {/* Active Filters */}
          {getActiveFiltersCount() > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="text-sm text-gray-500">Filtrat aktiv:</span>
              {selectedCategoryId && (
                <Badge variant="outline" className="flex items-center gap-1">
                  {activeCategoryName}
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('category');
                      setSearchParams(newParams);
                      setSelectedCategoryId('');
                    }}
                  />
                </Badge>
              )}
              {minPrice && (
                <Badge variant="outline" className="flex items-center gap-1">
                  Min: {parseInt(minPrice).toLocaleString()} €
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('minPrice');
                      setSearchParams(newParams);
                    }}
                  />
                </Badge>
              )}
              {maxPrice && (
                <Badge variant="outline" className="flex items-center gap-1">
                  Max: {parseInt(maxPrice).toLocaleString()} €
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('maxPrice');
                      setSearchParams(newParams);
                    }}
                  />
                </Badge>
              )}
              {location && (
                <Badge variant="outline" className="flex items-center gap-1">
                  {location}
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('location');
                      setSearchParams(newParams);
                    }}
                  />
                </Badge>
              )}
              {transportOption && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Truck size={12} className="mr-1" /> 
                  {transportOption === 'shipping_and_pickup'
                    ? 'Transport & Tërheqje'
                    : 'Vetëm Tërheqje'}
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('transport');
                      setSearchParams(newParams);
                      setSelectedTransportOption('');
                    }}
                  />
                </Badge>
              )}
              {make && (
                <Badge variant="outline" className="flex items-center gap-1">
                  Marka: {make}
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('make');
                      setSearchParams(newParams);
                      setSelectedMake('');
                    }}
                  />
                </Badge>
              )}
              {minYear && (
                <Badge variant="outline" className="flex items-center gap-1">
                  Viti nga: {minYear}
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('minYear');
                      setSearchParams(newParams);
                      setYearRange([1970, yearRange[1]]);
                    }}
                  />
                </Badge>
              )}
              {maxYear && parseInt(maxYear) < new Date().getFullYear() && (
                <Badge variant="outline" className="flex items-center gap-1">
                  Viti deri: {maxYear}
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('maxYear');
                      setSearchParams(newParams);
                      setYearRange([yearRange[0], new Date().getFullYear()]);
                    }}
                  />
                </Badge>
              )}
              {isPremium && (
                <Badge variant="outline" className="flex items-center gap-1">
                  Premium
                  <X
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('premium');
                      setSearchParams(newParams);
                    }}
                  />
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
                onClick={clearFilters}
              >
                Pastro të gjitha
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters - Desktop */}
            <div className="hidden lg:block">
              <SearchFilters
                minPrice={minPrice ? parseInt(minPrice) : 0}
                maxPrice={maxPrice ? parseInt(maxPrice) : 1000000}
                onPriceChange={(min, max) => {
                  const newParams = new URLSearchParams(searchParams);
                  if (min > 0) newParams.set('minPrice', min.toString());
                  else newParams.delete('minPrice');
                  if (max < 1000000) newParams.set('maxPrice', max.toString());
                  else newParams.delete('maxPrice');
                  setSearchParams(newParams);
                }}
                onCityChange={(city) => {
                  const newParams = new URLSearchParams(searchParams);
                  if (city) newParams.set('location', city);
                  else newParams.delete('location');
                  setSearchParams(newParams);
                  setSelectedLocation(city);
                }}
                onCategoryChange={handleCategoryChange}
                selectedCity={selectedLocation}
                selectedCategoryId={selectedCategoryId}
                onClear={clearFilters}
                isFiltered={getActiveFiltersCount() > 0}
                // Transport filter
                onTransportOptionChange={handleTransportOptionChange}
                selectedTransportOption={selectedTransportOption}
                // Vehicle-specific filters
                onMakeChange={selectedCategoryId === '7' ? handleMakeChange : undefined}
                onYearChange={selectedCategoryId === '7' ? handleYearChange : undefined}
                selectedMake={selectedMake}
                minYear={minYear ? parseInt(minYear) : 1970}
                maxYear={maxYear ? parseInt(maxYear) : new Date().getFullYear()}
              />
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              {/* Mobile Filters and Sort */}
              <div className="flex justify-between items-center mb-6 lg:hidden">
                <Sheet open={isMobileFiltersOpen} onOpenChange={setIsMobileFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <SlidersHorizontal size={16} />
                      <span>Filtrat</span>
                      {getActiveFiltersCount() > 0 && (
                        <Badge className="ml-1 bg-brand-500 hover:bg-brand-600">
                          {getActiveFiltersCount()}
                        </Badge>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filtrat</SheetTitle>
                    </SheetHeader>
                    <div className="py-4">
                      <SearchFilters
                        minPrice={minPrice ? parseInt(minPrice) : 0}
                        maxPrice={maxPrice ? parseInt(maxPrice) : 1000000}
                        onPriceChange={(min, max) => {
                          setPriceRange([min, max]);
                        }}
                        onCityChange={(city) => {
                          setSelectedLocation(city);
                        }}
                        onCategoryChange={(categoryId) => {
                          if (categoryId === selectedCategoryId) {
                            setSelectedCategoryId('');
                          } else {
                            setSelectedCategoryId(categoryId);
                          }
                        }}
                        selectedCity={selectedLocation}
                        selectedCategoryId={selectedCategoryId}
                        onClear={clearFilters}
                        isFiltered={getActiveFiltersCount() > 0}
                        // Transport filter
                        onTransportOptionChange={(option) => setSelectedTransportOption(option)}
                        selectedTransportOption={selectedTransportOption}
                        // Vehicle-specific filters
                        onMakeChange={selectedCategoryId === '7' ? (make) => setSelectedMake(make) : undefined}
                        onYearChange={selectedCategoryId === '7' ? (min, max) => setYearRange([min, max]) : undefined}
                        selectedMake={selectedMake}
                        minYear={minYear ? parseInt(minYear) : 1970}
                        maxYear={maxYear ? parseInt(maxYear) : new Date().getFullYear()}
                      />
                      <div className="flex gap-2 mt-6">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={clearFilters}
                        >
                          Pastro
                        </Button>
                        <Button
                          className="flex-1 bg-brand-500 hover:bg-brand-600"
                          onClick={applyFilters}
                        >
                          Apliko
                        </Button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
                <Select
                  value={sortBy}
                  onValueChange={(value) => {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('sort', value);
                    setSearchParams(newParams);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Rendit sipas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Më të rejat</SelectItem>
                    <SelectItem value="oldest">Më të vjetrat</SelectItem>
                    <SelectItem value="price_asc">Çmimi: I ulët në të lartë</SelectItem>
                    <SelectItem value="price_desc">Çmimi: I lartë në të ulët</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Desktop Sort */}
              <div className="hidden lg:flex justify-between items-center mb-6">
                <div>
                  {query && (
                    <h1 className="text-xl font-semibold">
                      Rezultatet për "{query}"
                    </h1>
                  )}
                  {selectedCategoryId && !query && (
                    <h1 className="text-xl font-semibold">
                      {activeCategoryName}
                    </h1>
                  )}
                  {!query && !selectedCategoryId && (
                    <h1 className="text-xl font-semibold">
                      Të gjitha produktet
                    </h1>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {listings.length} produkte të gjetura
                  </p>
                </div>
                <Select
                  value={sortBy}
                  onValueChange={(value) => {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('sort', value);
                    setSearchParams(newParams);
                  }}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Rendit sipas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Më të rejat</SelectItem>
                    <SelectItem value="oldest">Më të vjetrat</SelectItem>
                    <SelectItem value="price_asc">Çmimi: I ulët në të lartë</SelectItem>
                    <SelectItem value="price_desc">Çmimi: I lartë në të ulët</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Mobile Title */}
              <div className="lg:hidden mb-4">
                {query && (
                  <h1 className="text-xl font-semibold">
                    Rezultatet për "{query}"
                  </h1>
                )}
                {selectedCategoryId && !query && (
                  <h1 className="text-xl font-semibold">
                    {activeCategoryName}
                  </h1>
                )}
                {!query && !selectedCategoryId && (
                  <h1 className="text-xl font-semibold">
                    Të gjitha produktet
                  </h1>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  {listings.length} produkte të gjetura
                </p>
              </div>

              {/* Products Grid */}
              {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array(6)
                    .fill(0)
                    .map((_, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse"
                      >
                        <div className="aspect-[4/3] bg-gray-200" />
                        <div className="p-4 space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4" />
                          <div className="h-6 bg-gray-200 rounded w-1/2" />
                          <div className="h-4 bg-gray-200 rounded w-full" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : listings && listings.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {listings.map((listing) => (
                      <ProductCard
                        key={listing.id}
                        id={listing.id.toString()}
                        title={listing.title}
                        price={listing.price}
                        location={listing.location}
                        imageUrl={
                          listing.images && listing.images.length > 0
                            ? listing.images[0]
                            : ''
                        }
                        category={listing.categories?.name || ''}
                        isPremium={listing.is_premium}
                        createdAt={listing.created_at}
                        condition={listing.condition}
                        deliveryOption={listing.delivery_option}
                        user_id={listing.user_id}
                        stock={listing.stock}
                        is_price_negotiable={listing.is_price_negotiable}
                        // Vehicle-specific fields
                        make={listing.make}
                        model={listing.model}
                        year={listing.year}
                        mileage={listing.mileage}
                        transmission={listing.transmission}
                      />
                    ))}
                  </div>
                  
                  {/* Replace button with load more reference div */}
                  {hasNextPage && (
                    <div 
                      ref={loadMoreRef}
                      className="flex justify-center my-8"
                    >
                      {isFetchingNextPage ? (
                        <div className="flex items-center justify-center space-x-2 text-gray-500">
                          <div className="h-4 w-4 rounded-full border-2 border-t-transparent border-gray-500 animate-spin"></div>
                          <span>Duke ngarkuar...</span>
                        </div>
                      ) : (
                        <div className="h-8 w-full"></div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nuk u gjet asnjë produkt
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Provoni të ndryshoni filtrat ose të kërkoni diçka tjetër.
                  </p>
                  <Button onClick={clearFilters}>Pastro filtrat</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Search;
